import React, { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import {
  listAuditLogs,
  exportAuditLogs,
  AuditLog,
  AuditLogListParams,
  FieldChange,
} from "../../services/audit.service";
import CustomSelect from "../../components/common/CustomSelect";
import { paginationPageWindow } from "../../utils/pagination";
import {
  ClipboardList,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
  FileText,
  User,
  Calendar,
  Layers,
  Activity,
  X,
  RotateCcw,
  Filter,
} from "lucide-react";

const USER_ROLE_OPTIONS = [
  { label: "All Roles", value: "All" },
  { label: "Platform Admin", value: "Platform Admin" },
  { label: "Tenant Admin", value: "Tenant Admin" },
  { label: "Doctor", value: "Doctor" },
];

const CATEGORY_OPTIONS = [
  { label: "All Platforms", value: "All" },
  { label: "Platform (PF)", value: "Hospital Onboarding" },
  { label: "Doctor-Patient Platform (Alpha)", value: "Doctor Onboarding" },
  { label: "Legal", value: "Legal Content" },
];

const ENTITY_TYPE_OPTIONS = [
  { label: "All Entities", value: "All" },
  { label: "Hospital", value: "Hospital" },
  { label: "Doctor", value: "Doctor" },
  { label: "Legal Acceptance", value: "LEGAL_ACCEPTANCE" },
];

const ACTION_OPTIONS = [
  { label: "All Actions", value: "All" },
  { label: "Create", value: "Create" },
  { label: "Update", value: "Update" },
  { label: "Delete", value: "Delete" },
];

const AuditLogList: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<"excel" | "pdf" | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Toggle state for filter panel visibility (closed by default)
  const [showFilters, setShowFilters] = useState(false);

  // Expanded table row IDs for inline field changes
  const [expandedRowIds, setExpandedRowIds] = useState<Record<string, boolean>>({});

  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [performedBy, setPerformedBy] = useState("");
  const [userRole, setUserRole] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [entityType, setEntityType] = useState("All");
  const [actionFilter, setActionFilter] = useState("All");
  const [fieldName, setFieldName] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  const toggleRowExpand = (id: string) => {
    setExpandedRowIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: AuditLogListParams = { page, limit };
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (performedBy.trim()) params.performedBy = performedBy.trim();
      if (userRole && userRole !== "All") params.userRole = userRole;
      if (categoryFilter && categoryFilter !== "All") params.category = categoryFilter;
      if (entityType && entityType !== "All") params.entityType = entityType;
      if (actionFilter && actionFilter !== "All") params.action = actionFilter;
      if (fieldName.trim()) params.fieldName = fieldName.trim();

      const result = await listAuditLogs(params);
      setAuditLogs(result.auditLogs);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, dateFrom, dateTo, performedBy, userRole, entityType, actionFilter, fieldName]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const handleResetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setPerformedBy("");
    setUserRole("All");
    setCategoryFilter("All");
    setEntityType("All");
    setActionFilter("All");
    setFieldName("");
    setPage(1);
  };

  const handleExport = async (format: "excel" | "pdf") => {
    setExporting(format);
    try {
      const params: AuditLogListParams = {};
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (performedBy.trim()) params.performedBy = performedBy.trim();
      if (userRole && userRole !== "All") params.userRole = userRole;
      if (categoryFilter && categoryFilter !== "All") params.category = categoryFilter;
      if (entityType && entityType !== "All") params.entityType = entityType;
      if (actionFilter && actionFilter !== "All") params.action = actionFilter;
      if (fieldName.trim()) params.fieldName = fieldName.trim();

      await exportAuditLogs(params, format);
      toast.success(`Audit logs exported as ${format.toUpperCase()}`);
    } catch {
      toast.error(`Failed to export audit logs as ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Export Buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-end gap-4">
        {/* Export Actions */}
        <div className="flex items-center gap-3 self-start sm:self-auto">
          <button
            onClick={() => handleExport("pdf")}
            disabled={exporting !== null}
            className="flex items-center gap-2 h-11 bg-primary-600 text-white px-5 rounded-lg text-sm font-medium shadow-sm hover:bg-primary-700 transition-colors shrink-0 disabled:opacity-50"
          >
            {exporting === "pdf" ? (
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <FileText size={20} />
            )}
            Export PDF
          </button>

          <button
            onClick={() => handleExport("excel")}
            disabled={exporting !== null}
            className="flex items-center gap-2 h-11 bg-primary-600 text-white px-5 rounded-lg text-sm font-medium shadow-sm hover:bg-primary-700 transition-colors shrink-0 disabled:opacity-50"
          >
            {exporting === "excel" ? (
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <FileSpreadsheet size={20} />
            )}
            Export Excel
          </button>
        </div>
      </div>

      {/* Main Search & Filter Container */}
      <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-4 sm:p-5 space-y-4">
        {/* Top Search Bar & Filters Toggle */}
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by actor, entity, description, changed field..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-800 placeholder-gray-400"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowFilters((prev) => !prev)}
            className="flex items-center justify-center gap-2 px-4 h-10 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white shadow-sm transition-colors shrink-0"
          >
            <Filter size={16} />
            <span>Filters</span>
          </button>
        </form>

        {/* Collapsible Filter Panel (Shown only when showFilters is true) */}
        {showFilters && (
          <form onSubmit={handleFilterSubmit} className="pt-4 border-t border-gray-100 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Date From */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Date From
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-700"
                  />
                  <span
                    className={`pointer-events-none absolute top-px bottom-px left-px right-10 flex items-center pl-3 bg-white text-sm ${
                      dateFrom ? "text-gray-700" : "text-gray-400"
                    }`}
                  >
                    {dateFrom ? formatDateDisplay(dateFrom) : "DD/MM/YYYY"}
                  </span>
                </div>
              </div>

              {/* Date To */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Date To
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-700"
                  />
                  <span
                    className={`pointer-events-none absolute top-px bottom-px left-px right-10 flex items-center pl-3 bg-white text-sm ${
                      dateTo ? "text-gray-700" : "text-gray-400"
                    }`}
                  >
                    {dateTo ? formatDateDisplay(dateTo) : "DD/MM/YYYY"}
                  </span>
                </div>
              </div>

              {/* Performed By */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Performed By
                </label>
                <input
                  type="text"
                  value={performedBy}
                  onChange={(e) => setPerformedBy(e.target.value)}
                  placeholder="Actor name"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-700 placeholder-gray-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* User Role */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  User Role
                </label>
                <CustomSelect
                  value={userRole}
                  onChange={(val) => setUserRole(val)}
                  options={USER_ROLE_OPTIONS}
                />
              </div>

              {/* Platform */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Platform
                </label>
                <CustomSelect
                  value={categoryFilter}
                  onChange={(val) => setCategoryFilter(val)}
                  options={CATEGORY_OPTIONS}
                />
              </div>

              {/* Entity Type */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Entity Type
                </label>
                <CustomSelect
                  value={entityType}
                  onChange={(val) => setEntityType(val)}
                  options={ENTITY_TYPE_OPTIONS}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Action */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Action
                </label>
                <CustomSelect
                  value={actionFilter}
                  onChange={(val) => setActionFilter(val)}
                  options={ACTION_OPTIONS}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Field Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Field Name
                </label>
                <input
                  type="text"
                  value={fieldName}
                  onChange={(e) => setFieldName(e.target.value)}
                  placeholder="e.g., Forename"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-700 placeholder-gray-400"
                />
              </div>
            </div>

            {/* Filter Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleResetFilters}
                className="px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium transition-colors"
              >
                Clear Filters
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Audit Log Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-500 animate-pulse">Loading audit logs...</p>
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardList size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">No audit logs found</p>
            <p className="text-gray-400 text-sm mt-1">
              Try adjusting your date range or filter options
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50/80 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700 whitespace-nowrap">
                      Date &amp; Time
                    </th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700 whitespace-nowrap">
                      Performed By
                    </th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700 whitespace-nowrap">
                      User Role
                    </th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700 whitespace-nowrap">
                      Platform
                    </th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700 whitespace-nowrap">
                      Entity Type
                    </th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700 whitespace-nowrap">
                      Action
                    </th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700">
                      Description
                    </th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700 whitespace-nowrap">
                      Entity ID
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {auditLogs.map((log) => {
                    const hasChanges =
                      log.changes &&
                      typeof log.changes === "object" &&
                      Object.keys(log.changes).length > 0;
                    const isExpanded = !!expandedRowIds[log.id];

                    return (
                      <React.Fragment key={log.id}>
                        <tr className="hover:bg-gray-50/80 transition-colors">
                          {/* Date & Time */}
                          <td className="px-6 py-4 text-xs font-medium text-gray-700 whitespace-nowrap">
                            {log.formattedDateTime || log.createdAt}
                          </td>

                          {/* Performed By */}
                          <td className="px-6 py-4 text-xs font-medium text-gray-900 whitespace-nowrap">
                            {log.performedByName || "Admin User"}
                          </td>

                          {/* User Role */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-600">
                              {log.userRole || "Doctor"}
                            </span>
                          </td>

                          {/* Category */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <CategoryBadge category={log.category} />
                          </td>

                          {/* Entity Type */}
                          <td className="px-6 py-4 text-xs font-semibold text-gray-800 uppercase tracking-wide whitespace-nowrap">
                            {formatEntityType(log.entityType)}
                          </td>

                          {/* Action Badge */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <ActionBadge action={log.action} />
                          </td>

                          {/* Description */}
                          <td className="px-6 py-4 text-xs text-gray-700 min-w-[180px]">
                            {log.shortDescription || "No description provided"}
                          </td>

                          {/* Entity ID with Expand Chevron if changes exist */}
                          <td className="px-6 py-4 text-xs whitespace-nowrap">
                            {hasChanges ? (
                              <button
                                onClick={() => toggleRowExpand(log.id)}
                                className="inline-flex items-center gap-1.5 font-medium text-gray-800 hover:text-emerald-700 transition-colors group cursor-pointer"
                              >
                                <span>{formatEntityName(log.entityName) || log.entityId || "Details"}</span>
                                {isExpanded ? (
                                  <ChevronUp size={15} className="text-emerald-600 shrink-0" />
                                ) : (
                                  <ChevronDown size={15} className="text-emerald-600 shrink-0" />
                                )}
                              </button>
                            ) : (
                              <span className="text-gray-700 break-all font-mono text-[11px]">
                                {formatEntityName(log.entityName) ||
                                  log.entityId ||
                                  (typeof log.details === "string"
                                    ? log.details
                                    : log.details
                                    ? JSON.stringify(log.details)
                                    : "-")}
                              </span>
                            )}
                          </td>
                        </tr>

                        {/* Inline Accordion Field Differences Sub-table */}
                        {isExpanded && hasChanges && (
                          <tr className="bg-gray-50/60">
                            <td colSpan={8} className="px-6 py-3 border-b border-gray-200">
                              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-xs">
                                <table className="w-full text-left text-xs">
                                  <thead className="bg-gray-100 text-gray-600 font-bold border-b border-gray-200 uppercase tracking-wider">
                                    <tr>
                                      <th className="px-4 py-2.5 w-1/3">FIELD NAME</th>
                                      <th className="px-4 py-2.5 w-1/3">PREVIOUS VALUE</th>
                                      <th className="px-4 py-2.5 w-1/3">UPDATED VALUE</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {(() => {
                                      const expanded = expandFieldChanges(log.changes!);
                                      // Defensive fallback: if nothing expanded but changes exist,
                                      // render the raw old/new entries so the table never appears empty.
                                      const rowsToRender =
                                        expanded.length > 0
                                          ? expanded
                                          : Object.entries(log.changes!).map(([field, diff]) => ({
                                              field: formatFieldName(field),
                                              id: field,
                                              name: "",
                                              prev: formatVal(diff?.old),
                                              curr: formatVal(diff?.new),
                                              added: false,
                                              removed: false,
                                            }));
                                      return rowsToRender.map((row) => (
                                        <tr key={row.id} className="hover:bg-gray-50/50">
                                          <td className="px-4 py-2.5 font-medium text-gray-900">
                                            {row.field}
                                            {row.name && (
                                              <span className="block text-xs font-normal text-gray-400">
                                                {row.name}
                                              </span>
                                            )}
                                          </td>
                                          <td
                                            className={`px-4 py-2.5 text-gray-600 ${
                                              row.added ? "italic text-gray-400" : ""
                                            }`}
                                          >
                                            {row.prev}
                                          </td>
                                          <td
                                            className={`px-4 py-2.5 text-gray-900 font-semibold ${
                                              row.removed ? "italic text-gray-400" : ""
                                            }`}
                                          >
                                            {row.curr}
                                          </td>
                                        </tr>
                                      ));
                                    })()}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-sm text-gray-500">
                  Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <div className="flex items-center gap-1">
                    {paginationPageWindow(page, totalPages).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setPage(p)}
                        className={`h-8 w-8 rounded-lg text-sm font-medium border flex items-center justify-center transition-colors ${
                          page === p
                            ? "bg-primary-600 border-primary-600 text-white cursor-default"
                            : "bg-white border-gray-200 text-gray-500 hover:bg-primary-50 hover:text-primary-700"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* --- Helper Formatters --- */
const ENTITY_TYPE_LABELS: Record<string, string> = {
  MDT_REPORT_STRUCTURE: "MDT TEMPLATE",
};

const ENTITY_NAME_LABELS: Record<string, string> = {
  "MDT Report Structure": "MDT Template",
};

const formatEntityType = (type?: string): string => {
  if (!type) return "";
  return ENTITY_TYPE_LABELS[type] || type;
};

const formatEntityName = (name?: string): string => {
  if (!name) return "";
  return ENTITY_NAME_LABELS[name] || name;
};

const formatDateDisplay = (iso: string): string => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
};

const formatFieldName = (name: string) => {
  if (!name) return "";
  if (/^Section (added|removed): /i.test(name) || /^Section ".+": /i.test(name)) return name;
  return name
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

/**
 * Smart value formatter for audit log field-change display.
 * Parses JSON strings and renders objects/arrays as readable text
 * instead of raw JSON output.
 */
const formatVal = (val: any): string => {
  if (val === null || val === undefined || val === "") return "—";

  // Try to parse strings that look like JSON objects/arrays
  let parsed = val;
  if (typeof val === "string" && (val.startsWith("{") || val.startsWith("["))) {
    try {
      parsed = JSON.parse(val);
    } catch {
      // not valid JSON — treat as plain string
      return val;
    }
  }

  // Handle actual objects / parsed JSON
  if (typeof parsed === "object" && parsed !== null) {
    if (Array.isArray(parsed)) {
      if (parsed.length === 0) return "(empty)";
      return parsed
        .map((item: any) =>
          typeof item === "object" && item !== null
            ? formatObjectAsText(item)
            : String(item)
        )
        .join(", ");
    }
    return formatObjectAsText(parsed);
  }

  // Booleans
  if (typeof parsed === "boolean") return parsed ? "Yes" : "No";

  return String(parsed);
};

/** Converts a plain object into "Key: value · Key: value" readable format */
const formatObjectAsText = (obj: Record<string, any>): string => {
  return Object.entries(obj)
    .map(([k, v]) => {
      // Convert camelCase/snake_case keys to Title Case
      const label = k
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (s) => s.toUpperCase())
        .trim();

      let display: string;
      if (v === null || v === undefined || v === "") {
        display = "(empty)";
      } else if (typeof v === "boolean") {
        display = v ? "Yes" : "No";
      } else if (Array.isArray(v)) {
        display = v.length === 0 ? "(empty)" : v.join(", ");
      } else if (typeof v === "object") {
        display = formatObjectAsText(v);
      } else {
        display = String(v);
      }
      return `${label}: ${display}`;
    })
    .join("  ·  ");
};

interface DiffRow {
  field: string;
  id: string;
  name: string;
  prev: string;
  curr: string;
  added?: boolean;
  removed?: boolean;
}

// Stable identity for an array element (matches by key/title so reorder/rename don't fabricate diffs).
const elementKey = (el: any): string => {
  if (!el || typeof el !== "object") return String(el);
  if (el.key !== undefined) return String(el.key);
  if (el.title !== undefined) return String(el.title);
  return JSON.stringify(el);
};

const elementLabel = (el: any): string => {
  if (!el || typeof el !== "object") return String(el);
  if (el.label !== undefined) return String(el.label);
  if (el.title !== undefined) return String(el.title);
  if (el.key !== undefined) return String(el.key);
  return JSON.stringify(el);
};

// Human-friendly rendering of a single array element for a given top-level field.
const elementValue = (el: any, field: string): string => {
  if (el === null || el === undefined) return "-";
  if (field.toLowerCase() === "patientinfofields" && typeof el?.enabled === "boolean") {
    return `${el.label || elementLabel(el)}: ${el.enabled ? "On" : "Off"}`;
  }
  if (field.toLowerCase() === "sections" && typeof el === "object") {
    const body = typeof el.body === "string" && el.body ? el.body : "";
    return body ? body : "-";
  }
  if (typeof el === "object") return JSON.stringify(el);
  return el === "" ? "-" : String(el);
};

// Expand a top-level field diff into one row per scalar field or per changed array element,
// so array values (e.g. patientInfoFields, sections) render as a clean previous/current table
// instead of a single blob of JSON.
const expandFieldChanges = (
  changes: Record<string, { old: any; new: any }>
): DiffRow[] => {
  const rows: DiffRow[] = [];

  const pushScalar = (field: string, id: string, prev: any, curr: any) => {
    rows.push({
      field: formatFieldName(field),
      id,
      name: "",
      prev: formatVal(prev),
      curr: formatVal(curr),
    });
  };

  // Merge a single old/new (object or scalar) into per-element rows,
  // so arrays and objects render as one readable row per entry (never raw JSON).
  const expandValues = (field: string, oldVal: any, newVal: any, sub = "") => {
    const isCollection = (v: any) => v && typeof v === "object" && !Array.isArray(v)
      ? Object.keys(v).length > 0
      : false;
    const oldCol = Array.isArray(oldVal) ? oldVal : isCollection(oldVal) ? Object.values(oldVal) : null;
    const newCol = Array.isArray(newVal) ? newVal : isCollection(newVal) ? Object.values(newVal) : null;

    // Both sides plain scalars (or empty) → single row.
    if (oldCol === null && newCol === null) {
      pushScalar(field, `${field}:${sub}:scalar`, oldVal, newVal);
      return;
    }

    const oldList: any[] = oldCol ?? [];
    const newList: any[] = newCol ?? [];
    const oldById = new Map(oldList.map((e) => [elementKey(e), e]));
    const newById = new Map(newList.map((e) => [elementKey(e), e]));
    const ids = new Set<string>(Array.from(oldById.keys()).concat(Array.from(newById.keys())));

    ids.forEach((id) => {
      const oldEl = oldById.get(id);
      const newEl = newById.get(id);
      const label =
        oldEl !== undefined ? elementLabel(oldEl) : newEl !== undefined ? elementLabel(newEl) : id;
      const rowId = `${field}:${sub}:${id}`;

      if (oldEl !== undefined && newEl !== undefined) {
        if (JSON.stringify(oldEl) === JSON.stringify(newEl)) return; // unchanged
        rows.push({
          field: formatFieldName(field),
          id: rowId,
          name: label,
          prev: elementValue(oldEl, field),
          curr: elementValue(newEl, field),
        });
      } else if (newEl !== undefined) {
        rows.push({
          field: formatFieldName(field),
          id: rowId,
          name: label,
          prev: "-",
          curr: elementValue(newEl, field),
          added: true,
        });
      } else if (oldEl !== undefined) {
        rows.push({
          field: formatFieldName(field),
          id: rowId,
          name: label,
          prev: elementValue(oldEl, field),
          curr: "-",
          removed: true,
        });
      }
    });
  };

  Object.entries(changes).forEach(([field, diff]) => {
    expandValues(field, diff.old, diff.new);
  });

  return rows;
};

/* --- Action Badge Component --- */
const ActionBadge: React.FC<{ action: string }> = ({ action }) => {
  let classes = "bg-gray-100 text-gray-700 border-gray-200";
  const act = (action || "").toLowerCase();

  if (act === "create") {
    classes = "bg-emerald-50 text-emerald-700 border-emerald-200";
  } else if (act === "update") {
    classes = "bg-amber-50 text-amber-700 border-amber-200";
  } else if (act === "delete") {
    classes = "bg-rose-50 text-rose-700 border-rose-200";
  }

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${classes}`}
    >
      {action}
    </span>
  );
};

/* --- Platform Badge Component --- */
const CategoryBadge: React.FC<{ category?: string }> = ({ category }) => {
  const cat = (category || "Hospital Onboarding").trim().toLowerCase();

  const isDoctor = cat.includes("doctor");

  const label = isDoctor
    ? "Doctor-Patient Platform (Alpha)"
    : "Platform (PF)";

  const classes = isDoctor
    ? "bg-violet-50 text-violet-700 border-violet-200"
    : "bg-blue-50 text-blue-700 border-blue-200";

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${classes}`}
    >
      {label}
    </span>
  );
};

export default AuditLogList;
